import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';

export type AppCardVariant = 'default' | 'bordered' | 'elevated' | 'outlined';
export type AppCardPadding = 'none' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * AppCard - Componente de container padronizado
 *
 * @example
 * ```html
 * <app-card
 *   variant="elevated"
 *   padding="lg"
 *   [clickable]="true"
 *   (cardClicked)="handleCardClick()">
 *   <app-card-header>
 *     <h3>Título do Card</h3>
 *   </app-card-header>
 *   <app-card-content>
 *     Conteúdo do card aqui
 *   </app-card-content>
 *   <app-card-footer>
 *     <app-button>Ação</app-button>
 *   </app-card-footer>
 * </app-card>
 * ```
 */
@Component({
  selector: 'app-card',
  standalone: true,
  imports: [NgClass],
  template: `
    <div
      [ngClass]="computedClasses()"
      [attr.role]="clickable() ? 'button' : null"
      [attr.tabindex]="clickable() ? '0' : null"
      [attr.aria-label]="ariaLabel"
      (click)="handleClick($event)"
      (keydown.enter)="handleKeydown($any($event))"
      (keydown.space)="handleKeydown($any($event))"
    >
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      :host {
        @apply block;
      }

      div {
        @apply bg-white rounded-lg transition-all duration-200;
      }

      /* Variants */
      .variant-default {
        @apply border border-neutral-200;
      }

      .variant-bordered {
        @apply border-2 border-neutral-300;
      }

      .variant-elevated {
        @apply shadow-medium border border-neutral-100;
      }

      .variant-outlined {
        @apply border-2 border-primary-200;
      }

      /* Padding */
      .padding-none {
        @apply p-0;
      }

      .padding-sm {
        @apply p-3;
      }

      .padding-md {
        @apply p-4;
      }

      .padding-lg {
        @apply p-6;
      }

      .padding-xl {
        @apply p-8;
      }

      /* States */
      .clickable {
        @apply cursor-pointer;
      }

      .clickable:hover {
        @apply shadow-strong transform scale-[1.02];
      }

      .clickable:focus {
        @apply outline-none ring-2 ring-primary-500 ring-offset-2;
      }

      .loading {
        @apply opacity-75 cursor-wait;
      }
    `,
  ],
})
export class AppCardComponent {
  // Signals para estado
  variant = signal<AppCardVariant>('default');
  padding = signal<AppCardPadding>('md');
  clickable = signal(false);
  loading = signal(false);

  @Input() ariaLabel?: string;

  // Enhanced inputs
  @Input({ required: false }) set cardVariant(value: AppCardVariant) {
    this.variant.set(value);
  }

  @Input({ required: false }) set cardPadding(value: AppCardPadding) {
    this.padding.set(value);
  }

  @Input({ required: false }) set isClickable(value: boolean) {
    this.clickable.set(value);
  }

  @Input({ required: false }) set isLoading(value: boolean) {
    this.loading.set(value);
  }

  @Output() cardClicked = new EventEmitter<MouseEvent>();

  // Computed classes
  computedClasses = computed(() => {
    return [
      `variant-${this.variant()}`,
      `padding-${this.padding()}`,
      this.clickable() ? 'clickable' : '',
      this.loading() ? 'loading' : '',
    ]
      .filter(Boolean)
      .join(' ');
  });

  handleClick(event: MouseEvent): void {
    if (this.clickable() && !this.loading()) {
      this.cardClicked.emit(event);
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (
      this.clickable() &&
      !this.loading() &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      this.cardClicked.emit(event as unknown as MouseEvent);
    }
  }
}

/**
 * AppCardHeader - Cabeçalho do card
 */
@Component({
  selector: 'app-card-header',
  standalone: true,
  template: `
    <div class="card-header">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .card-header {
        @apply mb-4 pb-3 border-b border-neutral-200;
      }
    `,
  ],
})
export class AppCardHeaderComponent {}

/**
 * AppCardContent - Conteúdo do card
 */
@Component({
  selector: 'app-card-content',
  standalone: true,
  template: `
    <div class="card-content">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .card-content {
        @apply text-neutral-700 leading-relaxed;
      }
    `,
  ],
})
export class AppCardContentComponent {}

/**
 * AppCardFooter - Rodapé do card
 */
@Component({
  selector: 'app-card-footer',
  standalone: true,
  template: `
    <div class="card-footer">
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      .card-footer {
        @apply mt-4 pt-3 border-t border-neutral-200 flex items-center justify-end gap-3;
      }
    `,
  ],
})
export class AppCardFooterComponent {}
